/**
 * useSupportSession.ts
 * ----------------------------------------------------------------------------
 * Hook React para gerenciar a "Sessão de Suporte WL" — mecanismo que permite
 * um operador do painel White Label acessar e operar dentro de um tenant
 * cliente por um período limitado, sem virar membro do tenant.
 *
 * Backend:
 *   - RPC `wl_get_my_active_support_session` → sessão ativa do usuário logado
 *   - RPC `wl_start_support_session(p_tenant_id, p_reason, p_duration_minutes)`
 *   - RPC `wl_end_support_session()`
 *
 * Quando uma sessão está ativa, as funções `get_current_user_tenant_id` e
 * `get_validated_tenant_id` passam a retornar o tenant alvo da sessão para
 * o operador, fazendo com que toda a RLS existente funcione transparente.
 *
  * RELEASE: 2026-06-26 v2.3.0 - Polling de `wl_get_my_active_support_session`
 *                              passa a ser habilitado APENAS para operadores
 *                              WL (`useIsWlUser`). Usuários comuns não geram
 *                              mais requisições recorrentes desta RPC, pois
 *                              jamais possuem sessão de suporte ativa.
 *                              Também desativado `refetchOnWindowFocus` para
 *                              reduzir ruído mesmo entre operadores WL.
 * RELEASE: 2026-06-12 v2.2.0 - Novos campos opcionais `role` e `permissionProfileId`
 *                              em StartSupportSessionInput, propagados para a
 *                              RPC wl_start_support_session v2.2.0. Novo hook
 *                              useTenantSupportOptions para listar funções
 *                              (cargos) e perfis de permissão do tenant alvo.
 * RELEASE: 2026-05-30 v2.0.0 - Removido conceito de `scope` (read/write).
 *                              Sessão sempre dá acesso total; segurança
 *                              passa a ser via auditoria + acted_via_support_session_id.
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useIsWlUser } from "@/hooks/useIsWlUser";


export interface ActiveSupportSession {
  session_id: string;
  tenant_id: string;
  tenant_name: string;
  reason: string;
  started_at: string;
  expires_at: string;
}

/** Cargos selecionáveis ao iniciar sessão de suporte (root_admin é proibido). */
export type SupportSessionRole =
  | "tenant_admin"
  | "tenant_supervisor"
  | "agent"
  | "user";

export interface StartSupportSessionInput {
  tenantId: string;
  reason: string;
  durationMinutes: number;
  /**
   * Quando true (padrão), o vínculo temporário criado em tenant_members
   * para o operador WL no tenant alvo nasce com observer_mode_enabled=true.
   * Se o operador já for membro real do tenant, o flag NÃO é alterado
   * (preserva estado original).
   */
  observerMode?: boolean;
  /**
   * v2.2.0: Função (cargo) com a qual o operador será provisionado em
   * user_roles no tenant alvo. Default 'tenant_admin' (preserva
   * comportamento anterior). Ignorado se vínculo já existir.
   */
  role?: SupportSessionRole;
  /**
   * v2.2.0: Perfil de permissão (permission_profiles.id) atribuído ao
   * vínculo temporário. Default null (sem perfil). Precisa pertencer ao
   * tenant alvo e estar ativo. Ignorado se vínculo já existir.
   */
  permissionProfileId?: string | null;
}

export interface TenantSupportOptions {
  roles: Array<{ value: SupportSessionRole; label: string }>;
  permission_profiles: Array<{ id: string; name: string }>;
}

/**
 * Query da sessão de suporte ativa do operador logado.
 * Faz polling automático a cada 30s para detectar expiração no servidor.
 */
export function useActiveSupportSession() {
  const queryClient = useQueryClient();
  // v2.3.0: somente operadores WL (owner_wl/admin_wl/...) podem ter sessão
  // de suporte ativa. Para usuários comuns desabilitamos completamente a
  // query, eliminando o polling recorrente da RPC.
  const { isWlUser, isResolved: isWlResolved } = useIsWlUser();
  const enabled = isWlResolved && isWlUser;

  const q = useQuery<ActiveSupportSession | null>({
    queryKey: ["wl-active-support-session"],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("wl_get_my_active_support_session" as never)
        .maybeSingle();
      if (error) {
        logger.warn("[useActiveSupportSession] erro", String(error?.message ?? error));
        return null;
      }
      return (data as ActiveSupportSession | null) ?? null;
    },
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  });


  // Quando a sessão expirar localmente (relógio do cliente), invalida e
  // força recarga geral dos dados do tenant.
  useEffect(() => {
    if (!q.data?.expires_at) return;
    const remainingMs = new Date(q.data.expires_at).getTime() - Date.now();
    if (remainingMs <= 0) {
      queryClient.invalidateQueries({ queryKey: ["wl-active-support-session"] });
      return;
    }
    const t = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["wl-active-support-session"] });
    }, remainingMs + 1_000);
    return () => clearTimeout(t);
  }, [q.data?.expires_at, queryClient]);

  return q;
}

/**
 * Mutation para iniciar sessão de suporte.
 */
export function useStartSupportSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartSupportSessionInput) => {
      const { data, error } = await supabase.rpc(
        "wl_start_support_session" as never,
        {
          p_tenant_id: input.tenantId,
          p_reason: input.reason,
          p_duration_minutes: input.durationMinutes,
          // v2.1.0: default observer ligado quando não informado.
          p_observer_mode: input.observerMode ?? true,
          // v2.2.0: função (cargo) padrão = tenant_admin (compat. v2.1.0).
          p_role: input.role ?? "tenant_admin",
          // v2.2.0: perfil de permissão opcional (NULL quando não informado).
          p_permission_profile_id: input.permissionProfileId ?? null,
        } as never,
      );
      if (error) throw error;
      const arr = data as unknown;
      const row = Array.isArray(arr) ? arr[0] : arr;
      return row as {
        session_id: string;
        tenant_id: string;
        expires_at: string;
      };
    },
    onSuccess: () => {
      // Sessão ativa muda o tenant resolvido pela RLS — invalidamos TUDO.
      queryClient.invalidateQueries({ queryKey: ["wl-active-support-session"] });
      queryClient.invalidateQueries();
    },
  });
}

/**
 * v2.2.0: Hook para listar funções (cargos) e perfis de permissão
 * disponíveis para escolha no modal de início de sessão de suporte.
 * Chama a RPC `wl_get_tenant_support_options` no tenant alvo.
 */
export function useTenantSupportOptions(tenantId: string | null | undefined) {
  return useQuery<TenantSupportOptions>({
    queryKey: ["wl-tenant-support-options", tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "wl_get_tenant_support_options" as never,
        { p_tenant_id: tenantId } as never,
      );
      if (error) {
        logger.warn(
          "[useTenantSupportOptions] erro",
          String(error?.message ?? error),
        );
        throw error;
      }
      const obj = (data ?? {}) as Partial<TenantSupportOptions>;
      return {
        roles: Array.isArray(obj.roles) ? obj.roles : [],
        permission_profiles: Array.isArray(obj.permission_profiles)
          ? obj.permission_profiles
          : [],
      };
    },
  });
}

/**
 * Mutation para encerrar a sessão de suporte ativa.
 */
export function useEndSupportSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc(
        "wl_end_support_session" as never,
      );
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wl-active-support-session"] });
      queryClient.invalidateQueries();
    },
  });
}
